// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { StyledButton } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    priceAvailabilityLink?: InjectableCss;
    extraContent?: InjectableCss;
}

export const sharedClassName = "product-grid-container";

const productGridContainerStyle: ProductGridContainerStyle = {
    container: {
        css: css`
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 317px;
            border-radius: 0;
            background-color: transparent;
            box-shadow: none;
            box-sizing: border-box;
            ${xScreen("lte", "sm")} {
                margin: 0 auto;
            }
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }

            & > .top-section {
                margin-bottom: 8px;
                &:empty {
                    display: none;
                }
            }
            & > .middle-section {
                margin-bottom: 8px;
                flex: 1;
                &:empty {
                    display: none;
                }
            }
            & > .bottom-section {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 4px;
                justify-content: flex-end;
                &:empty {
                    display: none;
                }
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            min-width: 317px;
            max-width: 317px;
            min-height: 317px;
            max-height: 317px;
            height: 100%;
            margin: 0 auto;
            position: relative;
            ${StyledButton} {
                width: 100%;
                height: 100%;
                img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                }
            }
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            &:empty {
                display: none;
            }
        `,
    },
    price: {
        css: css`
            margin-bottom: auto;
            &:empty {
                display: none;
            }
        `,
    },
    availability: {
        css: css`
            margin-top: auto;
            &:empty {
                display: none;
            }
        `,
    },
    addToCartActions: {
        css: css`
            display: flex;
            margin-top: 4px;
            &:empty {
                display: none;
            }
        `,
    },
    addToList: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 8px;
            &:empty {
                display: none;
            }
        `,
    },
    extraContent: {
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
    ExtraContent = "ExtraContent",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
        getChild(childrenArray, NAMES.ExtraContent),
    ];
};

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const [styles, setStyles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList, extraContent] = React.useMemo(
        () => getDestructeredChildren(childrenArray),
        [childrenArray],
    );

    useEffect(() => {
        setStyles(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    }, [extendedStyles]);

    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps} className={sharedClassName}>
                <StyledWrapper className="top-section">{image}</StyledWrapper>
                <StyledWrapper className="middle-section">{info}</StyledWrapper>
                <StyledWrapper className="bottom-section">
                    {price}
                    {availability}
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
                {extraContent}
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} className={`${sharedClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};

const Price: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};
const ExtraContent: FC<PropsWithChildren<any>> = ({ children, ...otherProps }) => {
    const {
        styles: { extraContent },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...extraContent} className={`${sharedClassName}-extra`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;
ProductGridContainer.Info = Info;
ProductGridContainer.Info.displayName = NAMES.Info;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;
ProductGridContainer.ExtraContent = ExtraContent;
ProductGridContainer.ExtraContent.displayName = NAMES.ExtraContent;

export default ProductGridContainer;
